import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { TenantLogo } from '@/components/shell/TenantLogo';

import { useAuth } from './useAuth';

/**
 * Branded interstitial shown when a user authenticated at the IdP but their external identity is not
 * linked to any user here (PTV2-1448, capture-then-link). The gateway captured the sign-in as a PENDING
 * identity and redirected the browser here — no session is minted, because there is no user yet. The
 * user must ask an administrator to link their identity to an account; the only action offered is to
 * sign out (ending the IdP session so they can try a different account or return once linked).
 *
 * Rendered by {@link SessionGuard} on the {@code /identity-not-linked} path BEFORE the auth gate, since
 * the user has no session on this host.
 */
export function IdentityNotLinkedPage(): ReactElement {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  return (
    <main
      role="alert"
      className="flex min-h-screen items-center justify-center bg-muted/30 p-6"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-4 flex items-center gap-2.5">
            <TenantLogo />
            <span className="text-lg font-semibold tracking-tight text-brand">
              {t('shell.rail.brandName')}
            </span>
          </div>
          <CardTitle>{t('auth.identityNotLinked.heading')}</CardTitle>
          <CardDescription>{t('auth.identityNotLinked.message')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              void signOut();
            }}
          >
            {t('auth.signOut')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
